import styled from "styled-components";
import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { ClipLoaderCommon, ConfirmComponent, ProcessOrderHeader } from "@components";
import { DURATION_TIME, SIGN_IN_PROMPT_SEEN_KEY } from "@constants";
import {
    useAppSelector,
    openToast,
    AppDispatch,
    selectCheckout,
    selectRedeemer,
    fetchRedeemer,
    createNewRedeemer,
    selectAuth,
    loginV2,
    setJwt,
    initializeAccountDetail,
    store,
} from "@redux";
import { AppModuleLayout } from "@layouts";
import { breakpoint, lsEmail, sessionHelper, ssCheckoutFlow, theme } from "@utilities";
import { lsJwt, ssStorage } from "src/Utilities/storage";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { LoadingStage } from "src/Interfaces/shared";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { CartBreadcrumbs, PATH_BOOKING, PATH_HOME } from "@routes";
import {
    IAuthLogin,
    ICartItemData,
    IGuestNested,
    IPayNowResponseData,
    ISetCustomerData,
} from "@interfaces";
import { extractGuests, mergeAddressBookSuggestions } from "src/Utilities/addressBook";
import _ from "lodash";
import {
    CheckoutCartStepLabels,
    buildCartCheckoutContext,
    transformCartItemsToUpdateCartItemsData,
} from "./checkoutHelpers";
import { CheckoutStep1 } from "./Components/CheckoutStep1";
import { CheckoutStep2 } from "./Components/CheckoutStep2";
import { fetchCartItems, removeCartItemAndFetch, submitCartDataAndFetch } from "./checkout";
import { authApi, cartApi } from "@api";
import { CheckoutProvider } from "./CheckoutContext";
import { useStepPersistence } from "./CheckoutStepHelper";
import { useGoogleOAuth } from "./hooks/useGoogleOAuth";
import { useGuestCheckout } from "./hooks/useGuestCheckout";
import { GuestLoginModal } from "./Components/GuestLoginModal";
import { SignInBottomSheetModal } from "./Components/SignInBottomSheetModal";
import { shouldPromptSignup } from "./signupPromptGate";
import { reconcileStashWithCart } from "./guestCartStash";
import { PaymentRedirectOverlay } from "./Components/PaymentRedirectOverlay";
import { isBookingDateInPast } from "./bookingDateHelpers";

export const Cart = () => {
    //page hook
    const dispatch = useDispatch<AppDispatch>();
    const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    //redux state
    const { cartItems, cartItemsLoadingStage, customersLoadingStage, activeDiscount } =
        useAppSelector(selectCheckout);
    const cartCustomers = useAppSelector(selectRedeemer);
    const { jwt } = useAppSelector(selectAuth);
    const isLoggedIn = useMemo(() => !_.isNil(jwt), [jwt]);
    const [addressBookGuests, setAddressBookGuests] = useState<IGuestNested[]>([]);
    const suggestionCustomers = useMemo(
        () =>
            isLoggedIn
                ? mergeAddressBookSuggestions(cartCustomers, addressBookGuests)
                : cartCustomers,
        [isLoggedIn, cartCustomers, addressBookGuests],
    );

    const createGuestAccountPostOrder = async (email: string) => {
        try {
            const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
            let randomPassword = "";
            for (let i = 0; i < 16; i++) {
                randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            const signUpRes = await authApi.signUp({
                email,
                password: randomPassword,
                is_password_randomly_generated: true,
            });
            if (signUpRes.data.code === 200) {
                const loginRes = (await dispatch(loginV2({ email, password: randomPassword }))) as {
                    payload: IAuthLogin;
                };
                if (loginRes.payload.data?.accessToken) {
                    lsJwt.setItem(loginRes.payload.data);
                    lsEmail.setItem(email);
                    dispatch(setJwt(loginRes.payload.data));
                    await dispatch(initializeAccountDetail()).unwrap();
                    authApi.resendVerificationOtp().catch(console.error);
                }
            }
        } catch (err) {
            console.error("Post-order guest account creation failed:", err);
        }
        sessionStorage.removeItem("guestPrimaryEmail");
    };

    const sessionId = useMemo(() => {
        if (isLoggedIn) {
            return undefined;
        }
        const urlSessionId = searchParams.get("sessionId");
        if (urlSessionId) {
            return urlSessionId;
        }
        if (sessionHelper.hasSession()) {
            return sessionHelper.getSessionId();
        }
        return undefined;
    }, [searchParams, isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn && sessionId && !searchParams.get("sessionId")) {
            setSearchParams({ sessionId }, { replace: true });
        }
    }, [sessionId, isLoggedIn]);

    // Handle Google OAuth callback at page level
    useGoogleOAuth({
        isProcessingOAuth,
        setIsProcessingOAuth,
        sessionId,
    });

    //page state

    // Check if cart items have booking_data
    const hasBookingData = useMemo(() => {
        return cartItems.some((item) => item.booking_data && item.booking_data.length > 0);
    }, [cartItems]);

    const hasValidItems = useMemo(() => {
        return !cartItems.some((item) => {
            // Check product/availability flags
            if (item.is_product_latest === false || item.is_availability_latest === false) {
                return true;
            }
            // Check if booking date has passed. Shared with the Checkout step's
            // own expiry check — see isBookingDateInPast for why they must agree.
            return isBookingDateInPast(item.booking_date);
        });
    }, [cartItems]);

    const isLoading =
        cartItemsLoadingStage === LoadingStage.IS_INITIALIZING ||
        customersLoadingStage === LoadingStage.IS_INITIALIZING;

    const { activeStepIndex, setActiveStep } = useStepPersistence(
        hasBookingData && hasValidItems,
        isLoading,
        "cart-step",
        !hasValidItems,
    );

    const [isDeleting, setIsDeleting] = useState(false);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);

    /**
     * A signed-out visitor always starts on the cart list.
     *
     * The step is persisted in localStorage, so someone who reached the Checkout
     * step in an earlier session — or before signing out — is dropped straight
     * back into it as a guest, past the sign-up gate on the Checkout button. That
     * step then fires authenticated calls that answer 401. Applied once on
     * arrival rather than continuously, so it cannot fight the advance that
     * follows a successful sign-up.
     */
    const hasCheckedGuestStep = useRef(false);

    /**
     * Sign-up is asked for once, at the first step that actually needs an account.
     *
     * A guest reaches the cart freely from Add to Cart, and edits or removes items
     * there signed out; pressing Checkout is the point where the booking has to
     * belong to someone.
     *
     * The guest's edits are saved *before* the sheet opens, and only the intent to
     * advance is parked. Signing up merges the guest cart into the new account and
     * refetches it, which renumbers the rows — so a payload captured beforehand
     * refers to item ids that no longer exist, and replaying it answers 400
     * Validation Error on every row.
     */
    const [showCheckoutSignIn, setShowCheckoutSignIn] = useState(false);
    const pendingCheckoutRef = useRef<{ shouldGoToNextStep: boolean } | null>(null);

    const goToCheckoutStep = () => {
        setActiveStep(
            _.findIndex(
                CheckoutCartStepLabels,
                (label) => label.trim().toLowerCase() === "checkout",
            ),
            { hasItems: true },
        );
    };

    // Keeps the replay queue in step with what the guest actually has: removing a
    // product from the cart must also stop it being posted back at sign-up.
    useEffect(() => {
        if (isLoggedIn || isLoading) return;
        reconcileStashWithCart(cartItems);
    }, [isLoggedIn, isLoading, cartItems]);

    useEffect(() => {
        if (hasCheckedGuestStep.current || isLoading) return;
        hasCheckedGuestStep.current = true;
        if (!isLoggedIn && activeStepIndex !== 0) {
            setActiveStep(0);
        }
    }, [isLoading, isLoggedIn, activeStepIndex, setActiveStep]);

    const requestCheckout = async (items: ICartItemData[], shouldGoToNextStep: boolean) => {
        if (
            shouldPromptSignup("cartCheckout", {
                isLoggedIn,
                promptSeen: !!sessionStorage.getItem(SIGN_IN_PROMPT_SEEN_KEY),
            })
        ) {
            // Persisted against the guest session, which still owns these rows.
            // Not advancing yet: the sheet comes first.
            await handleSaveItems(items, "checkout", false);
            pendingCheckoutRef.current = { shouldGoToNextStep };
            setShowCheckoutSignIn(true);
            return;
        }
        await handleSaveItems(items, "checkout", shouldGoToNextStep);
    };

    const guestCheckout = useGuestCheckout({
        sessionId,
        customers: cartCustomers,
        isLoggedIn,
    });

    // unified delete queue
    const [deleteQueue, setDeleteQueue] = useState<
        { id: string | number; type: "cart" | "group" | "product" | "item" }[]
    >([]);

    useEffect(() => {
        if (cartItemsLoadingStage === LoadingStage.INITIALIZE_FAILURE) {
            ErrorToast({ Message: "Failed to load items" });
        }
    }, [cartItemsLoadingStage]);

    useEffect(() => {
        if (customersLoadingStage === LoadingStage.INITIALIZE_FAILURE) {
            ErrorToast({ Message: "Failed to load customers" });
        }
    }, [customersLoadingStage]);

    useEffect(() => {
        if (
            cartItems.length === 0 &&
            cartItemsLoadingStage !== LoadingStage.NOT_INITIALIZED &&
            cartItemsLoadingStage !== LoadingStage.IS_INITIALIZING &&
            cartItemsLoadingStage !== LoadingStage.IS_LOADING
        ) {
            dispatch(
                openToast({
                    type: "error",
                    message: `Cart is empty`,
                    autoHideDuration: DURATION_TIME,
                }),
            );
        }
    }, [cartItems.length, cartItemsLoadingStage]);

    const hasCartMounted = useRef(false);
    useEffect(() => {
        const shouldFetch =
            !hasCartMounted.current || cartItemsLoadingStage === LoadingStage.NOT_INITIALIZED;

        if (shouldFetch) {
            hasCartMounted.current = true;
            if (sessionId) {
                dispatch(fetchCartItems(sessionId));
            } else if (isLoggedIn) {
                dispatch(fetchCartItems(undefined));
            }
        }
    }, [sessionId, isLoggedIn, cartItemsLoadingStage, dispatch]);

    const scrollToTop = () => {
        window.scrollTo(0, 0);
    };

    const handleSaveItems = async (
        items: ICartItemData[],
        nextStep: string,
        shouldGoToNextStep: boolean,
    ) => {
        const updateItems = transformCartItemsToUpdateCartItemsData(items);

        // Get optionalFields from cart items for mapping fieldId to commonName
        const optionalFields = items.flatMap((item) => item.booking_details?.optionalFields || []);

        await dispatch(
            submitCartDataAndFetch({
                items: updateItems,
                sessionId,
                customers: cartCustomers,
                optionalFields,
            }),
        ).unwrap();

        if (shouldGoToNextStep) {
            // After saving items successfully, we know the cart has items with redeemers
            // Pass overrides to avoid stale closure issues where hasRedeemers might be false
            setActiveStep(
                _.findIndex(
                    CheckoutCartStepLabels,
                    (label) => label.trim().toLowerCase() === nextStep,
                ),
                { hasItems: true },
            );
        }
    };

    const proceedWithOrderCreate = async (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
        overrideSessionId?: string,
    ) => {
        const orderSessionId = overrideSessionId || sessionId;
        setIsCreatingOrder(true);
        try {
            const createOrderResponse = await cartApi.createOrder(
                { paymentType, pointsApplied, commissionApplied },
                orderSessionId,
            );

            const { is_payment_required, bookingReference } = createOrderResponse.data
                .data as IPayNowResponseData & {
                is_payment_required: boolean;
                bookingReference: string;
            };

            if (!is_payment_required) {
                const res = await cartApi.completeOrder(bookingReference);
                if (res?.data?.code === 200) {
                    sessionStorage.removeItem("guestCheckoutInProgress");
                    const guestEmail = sessionStorage.getItem("guestPrimaryEmail");
                    if (guestEmail) {
                        await createGuestAccountPostOrder(guestEmail);
                    }
                    navigate(PATH_BOOKING);
                } else {
                    throw new Error("Failed to complete order");
                }
                return;
            }

            const paymentLink = (
                createOrderResponse.data.data as IPayNowResponseData
            ).payNow.redirectUrl.replaceAll("\\", "");

            ssCheckoutFlow.setItem({ type: "cart", sessionId: orderSessionId });

            const state = store.getState();
            if (state.auth?.jwt) {
                const tempJwt = ssStorage("tempJwt");
                tempJwt.setItem(state.auth.jwt);
            }

            window.location.href = paymentLink;
        } catch (error: any) {
            ErrorToast({ Message: error?.response?.data?.title });
        } finally {
            setIsCreatingOrder(false);
        }
    };

    const handleOrderCreate = async (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
        overrideSessionId?: string,
    ) => {
        const orderArgs = { paymentType, pointsApplied, commissionApplied, overrideSessionId };
        await guestCheckout.ensureGuestAccount(
            () =>
                proceedWithOrderCreate(
                    paymentType,
                    pointsApplied,
                    commissionApplied,
                    overrideSessionId,
                ),
            orderArgs,
            setIsCreatingOrder,
        );
    };

    const handleHeaderBack = () => {
        if (activeStepIndex > 0) {
            setActiveStep(activeStepIndex - 1);
            return;
        }
        window.history.back();
    };

    const showDeletePopUp = (
        items: { id: string | number; type: "cart" | "group" | "product" | "item" }[],
    ) => {
        setDeleteQueue(items);
    };

    const handleDeleteYes = async () => {
        if (!deleteQueue.length) return;
        setIsDeleting(true);
        const isFullCartClear = deleteQueue.some(({ type }) => type === "cart");
        try {
            const results = await Promise.allSettled(
                deleteQueue.map(({ id, type }) =>
                    dispatch(removeCartItemAndFetch({ id, type, sessionId })).unwrap(),
                ),
            );
            const errors = results.filter((r) => r.status === "rejected");
            if (errors.length) {
                ErrorToast({
                    Message:
                        errors.length > 1 ? "Failed to delete some items" : "Failed to delete item",
                });
            } else if (isFullCartClear) {
                sessionStorage.removeItem("guestCheckoutInProgress");
                sessionStorage.removeItem("guestPrimaryEmail");
                sessionStorage.removeItem(SIGN_IN_PROMPT_SEEN_KEY);
                sessionStorage.removeItem("tempJwt");
                localStorage.removeItem("checkout-step");
                setActiveStep(0);
            }
            await dispatch(fetchCartItems(sessionId)).unwrap();
        } catch (e) {
            console.error(e);
            ErrorToast({ Message: "Failed to delete items" });
        } finally {
            setIsDeleting(false);
            setDeleteQueue([]);
        }
    };

    const onCreateNewCustomer = (newCustomerData: ISetCustomerData) => {
        return new Promise<any>((resolve, reject) => {
            const createRedeemer = async () => {
                const newCustomer = await dispatch(
                    createNewRedeemer({ customerData: newCustomerData, sessionId }),
                ).unwrap();
                await dispatch(fetchRedeemer(sessionId)).unwrap();
                resolve(newCustomer);
            };

            createRedeemer().catch(reject);
        });
    };

    useEffect(() => {
        if (isCreatingOrder) return;
        if (!isLoggedIn && !sessionId) return;
        dispatch(fetchCartItems(sessionId));
        dispatch(fetchRedeemer(sessionId));
        setTimeout(() => scrollToTop(), 0);
    }, [sessionId]);

    useEffect(() => {
        if (!isLoggedIn) {
            setAddressBookGuests([]);
            return;
        }
        let cancelled = false;
        cartApi
            .getCustomers()
            .then((resp) => {
                if (!cancelled) setAddressBookGuests(extractGuests(resp));
            })
            .catch(() => {
                if (!cancelled) setAddressBookGuests([]);
            });
        return () => {
            cancelled = true;
        };
    }, [isLoggedIn]);

    const renderComponent = () => {
        const activeStep = CheckoutCartStepLabels[activeStepIndex];

        switch (activeStep) {
            case "Checkout":
                return (
                    <CheckoutStep2
                        items={cartItems}
                        moveTo={setActiveStep}
                        customers={suggestionCustomers}
                        onItemsSave={(items, shouldGoToNextStep) =>
                            handleSaveItems(items, "checkout", shouldGoToNextStep)
                        }
                        onCreateNewCustomer={onCreateNewCustomer}
                        handleOrderCreate={handleOrderCreate}
                    />
                );
            case "Cart":
                return (
                    <CheckoutStep1
                        handleDeleteItems={(ids) =>
                            showDeletePopUp(ids.map((id) => ({ id, type: "item" })))
                        }
                        handleDeleteProduct={(prodId) => {
                            const itemIds = cartItems
                                .filter(
                                    (item) => item.tdms_product_id.toString() === prodId.toString(),
                                )
                                .map((item) => ({ id: item.id, type: "item" as const }));
                            if (itemIds.length > 0) {
                                showDeletePopUp(itemIds);
                            }
                        }}
                        handleDeleteCart={() => showDeletePopUp([{ id: "", type: "cart" }])}
                        onItemsSave={requestCheckout}
                    />
                );
        }
    };

    return (
        <AppModuleLayout onBack={handleHeaderBack}>
            <ProcessOrderHeader
                stepLabels={CheckoutCartStepLabels}
                activeStepIndex={activeStepIndex}
                breadcrumbs={CartBreadcrumbs}
                changeStep={setActiveStep}
            />
            <Main>
                {cartItemsLoadingStage === LoadingStage.IS_INITIALIZING ||
                cartItemsLoadingStage === LoadingStage.IS_LOADING ||
                customersLoadingStage === LoadingStage.IS_INITIALIZING ? (
                    <Content>
                        <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
                    </Content>
                ) : cartItems.length === 0 ? (
                    <Container className="wrapper">
                        <Content>
                            <img
                                width={65}
                                height={65}
                                src="/images/cart/emptyCart.svg"
                                alt="emptyCartLogo"
                            />
                            <p>There are no items in the Cart</p>
                            <NavLink to={PATH_HOME}>
                                <Button>Continue Browsing</Button>
                            </NavLink>
                        </Content>
                    </Container>
                ) : (
                    <CheckoutProvider
                        initialItems={cartItems}
                        checkoutContext={buildCartCheckoutContext()}
                        activeDiscount={activeDiscount?.discount}
                        sessionId={sessionId}
                    >
                        {renderComponent()}
                    </CheckoutProvider>
                )}
                <ConfirmComponent
                    itemIds={deleteQueue}
                    isDeleting={isDeleting}
                    onClickOverlay={() => setDeleteQueue([])}
                    onClickBtnYes={handleDeleteYes}
                    onClickBtnNo={() => setDeleteQueue([])}
                >
                    {isDeleting
                        ? "Deleting items from cart"
                        : `Are you sure you want to delete ${
                              deleteQueue.length > 1 ? "these items" : "this item"
                          }?`}
                </ConfirmComponent>
                {showCheckoutSignIn && (
                    <SignInBottomSheetModal
                        isOpen={showCheckoutSignIn}
                        sessionId={sessionId}
                        onClose={() => {
                            setShowCheckoutSignIn(false);
                            pendingCheckoutRef.current = null;
                        }}
                        onLoginSuccess={() => {
                            sessionStorage.setItem(SIGN_IN_PROMPT_SEEN_KEY, "true");
                            setShowCheckoutSignIn(false);
                            const pending = pendingCheckoutRef.current;
                            pendingCheckoutRef.current = null;
                            // Only the step moves. The items were saved before the
                            // sheet opened, and the sign-in path has just merged and
                            // refetched the cart under the new account.
                            if (pending?.shouldGoToNextStep) goToCheckoutStep();
                        }}
                    />
                )}
                {guestCheckout.showGuestLoginModal && (
                    <GuestLoginModal
                        email={guestCheckout.guestLoginEmail}
                        password={guestCheckout.guestLoginPassword}
                        onPasswordChange={guestCheckout.setGuestLoginPassword}
                        showPassword={guestCheckout.showGuestLoginPassword}
                        onToggleShowPassword={() =>
                            guestCheckout.setShowGuestLoginPassword(
                                !guestCheckout.showGuestLoginPassword,
                            )
                        }
                        isLoggingIn={guestCheckout.isGuestLoggingIn}
                        onLogin={() =>
                            guestCheckout.handleGuestLogin(async () => {
                                if (guestCheckout.pendingOrderArgs) {
                                    const args = guestCheckout.pendingOrderArgs;
                                    await proceedWithOrderCreate(
                                        args.paymentType,
                                        args.pointsApplied,
                                        args.commissionApplied,
                                        args.overrideSessionId,
                                    );
                                }
                            })
                        }
                        onClose={guestCheckout.closeGuestLoginModal}
                        onForgotPassword={() => window.open("/forgot-password", "_blank")}
                    />
                )}
                {isCreatingOrder && <PaymentRedirectOverlay />}
            </Main>
        </AppModuleLayout>
    );
};

const Main = styled.div`
    flex: 1;
    align-items: center;
    background-color: #f2f2f2;

    ${breakpoint.breakTablet`
        padding-bottom: 30px;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
    `}
`;

const Container = styled.div`
    background-color: white;
    display: flex;
    flex: 1;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    padding: 30px;
`;

const Content = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 14px;
`;

const Button = styled.button`
    border: 1px solid ${theme.colors.main};
    padding: 12px 24px;
    border-radius: 4px;
    color: ${theme.colors.button_text1};
    background: none;
    cursor: pointer;
`;
